import { IMAGE_LOADER, type ImageLoaderConfig } from '@angular/common';
import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
} from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideAuth } from '@core/auth';
import { provideIcons } from '@core/icons';
import { IntercomService } from '@core/intercom';
import { LocalStorage, provideLocalStorage } from '@core/local-storage';
import { provideMedia } from '@core/media';
import { provideTheming } from '@core/theming';
import { provideWindow } from '@core/window';
import { environment } from '@environments/environment';
import { NotificationsStreamService } from '@layout/data/notifications-stream';
import { TranslocoMatPaginatorIntl } from '@ui/paginator-intl';
import { provideSwissDateAdapter } from '@utils/date-adapter';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideHttpClient(withFetch()),

    // Angular Material
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    provideSwissDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'fr-CH' },
    { provide: MatPaginatorIntl, useClass: TranslocoMatPaginatorIntl },

    // Auth
    provideAuth(environment.oauth, [environment.api, environment.api_media]),

    /**
     * The portal's media loader. The image service behind `api_media` takes its
     * whole request as one base64 segment — bucket, key, and the resize it
     * should perform — so an `&lt;img>` asks for the size it will actually display
     * instead of downloading a full-resolution photo and scaling it in the
     * browser. Same encoding the owner portal and the legacy backoffice use.
     *
     * An image given no `loaderParams` falls through to its own `src`, because
     * not every image in the portal lives in a bucket: the actions panel renders
     * user photos that are already absolute URLs, and a loader that ignored
     * `src` would break them.
     */
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        const bucket = config.loaderParams?.['bucket'];
        if (!bucket) return config.src;

        const request = btoa(
          JSON.stringify({
            bucket,
            key: config.loaderParams?.['key'],
            edits: {
              resize: {
                width: config.width,
                height: config.loaderParams?.['height'],
                fit: 'contain',
              },
            },
          })
        );

        return `${environment.api_media}/${request}`;
      },
    },

    // BuilderKit
    provideIcons(),
    provideLocalStorage(),
    provideMedia(),
    // Nothing in the portal lets a user pick colours, so the stored hex
    // values are only a stale copy of the config below. Drop them before
    // Theming reads them, so a colour change here reaches every browser.
    // The scheme (light/dark/system) is the user's own choice and stays.
    provideAppInitializer(() => {
      const storage = inject(LocalStorage);
      for (const key of ['primaryColor', 'errorColor', 'neutralColor']) {
        storage.removeItem(key);
      }
    }),
    provideTheming({
      scheme: 'system',
      primary: '#D79256',
      error: '#E5484D',
      neutral: '#8B8D98',
      borderRadius: '0',
    }),
    provideWindow(),

    // Transloco
    // Transloco configuration
    provideTransloco({
      config: {
        availableLangs: [
          { id: 'en', label: 'English' },
          { id: 'fr-CH', label: 'Français' },
        ],
        defaultLang: 'fr-CH',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideTranslocoLocale({
      defaultCurrency: 'CHF',
      localeToCurrencyMapping: {
        'fr-CH': 'CHF',
        en: 'CHF',
      },
    }),

    // Start listening to the notifications SSE stream at app bootstrap.
    provideEnvironmentInitializer(() => {
      inject(NotificationsStreamService);
    }),

    // Boot the Intercom messenger once the account resolves.
    provideEnvironmentInitializer(() => {
      inject(IntercomService);
    }),
  ],
};
